import { Card, Col, Form, Row, Button } from 'react-bootstrap';
import { ChangedProperty, keyValue } from '../../../redux/types/types';
import { InformationIcon } from '../../shared/InformationIcon';
import { LockedOrChanged } from '../../shared/LockedOrChanged';
import {
  getBooleanOptions,
  getChangedInformation,
  getOptions,
  getOptionsLabel,
  hasChanged,
  shouldBeLocked,
} from '../common';
import { t } from '../../../services/translationService';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useFieldArray } from 'react-hook-form';
import { formatToSwedishDecimal, parseSwedishDecimal } from '../../../services/editBookService';

interface Props {
  errors: any;
  register: any;
  blockRef: any;
  setValue: any;
  getValues: any;
  pageVariant: string;
  lockedFields: keyValue[];
  changedProps: ChangedProperty[];
  watch: any;
  control: any;
  trigger: any;
  setIsEpub?: Dispatch<SetStateAction<boolean>>
}

export const Format = ({
  errors,
  register,
  blockRef,
  setValue,
  getValues,
  control,
  pageVariant,
  lockedFields,
  changedProps,
  watch,
  setIsEpub
}: Props) => {

  const showExtendedChangeInfo = pageVariant !== "publisher";
  const [initialRender, setInitialRender] = useState(true);

  const [bandtyper, setBandtyper] = useState([]);
  const [selectedBandtyp, setSelectedBandtyp] = useState<any[]>();
  const [currentMediaTyp, setCurrentMediaTyp] = useState<any>({});
  const [selectedUtgavetyp, setSelectedUtgavetyp] = useState<any>();

  const mediatyp: string | undefined = watch('mediatyp');
  const bandtyp: string | undefined = watch('bandtyp');

  // On switch media type, reset formats
  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
    } else {
      setValue('bandtyp', undefined);
      setSelectedBandtyp(undefined);
      setValue('epubversion', undefined);
    }

    const selectedMediaTyp = Object.values(
      window.bokinfo.dictionaries.mediaTypes
    ).find((m: any) => m.value === mediatyp) as any;

    selectedMediaTyp && setCurrentMediaTyp(selectedMediaTyp);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediatyp]);

  useEffect(() => {
    !!setIsEpub && setIsEpub(mediatyp === 'Nedladdningsbar bok' && bandtyp === 'Epub');
  }, [mediatyp, bandtyp, setIsEpub])

  useEffect(() => {
    const properties = ["bandtyp", "viktExklusiveForpackning"]

    // Use Object.fromEntries to create an object from key-value pairs
    // Map over the properties array to create an array of [key, value] pairs
    const values = Object.fromEntries(properties.map((property, index) => [property, getValues(properties)[index]]));

    // Bandtyp is set as string value on book, but is used as key/value in select & typeahead
    // so we need to set it as an array on mount
    if (values.bandtyp) {
      if (Array.isArray(values.bandtyp)) {
        setSelectedBandtyp(values.bandtyp)
      }
      else {
        setSelectedBandtyp([values.bandtyp])
      }
    }

    setValue("viktExklusiveForpackning", formatToSwedishDecimal(values.viktExklusiveForpackning))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // This will fire on any change to the field, but will only contain a value when something is actually selected.
  const onTypeaheadSelect = (value: any) => {
    setSelectedBandtyp(value);
    if (value?.length && value[0]?.value) {
      setValue("bandtyp", value[0].value)
    }
  }

  const getBandTypes = () => {
    if (mediatyp !== '' && mediatyp !== undefined) {
      const list = window.bokinfo.dictionaries['mediaTypes'];
      const result = list.filter((obj: any) => obj.key === mediatyp);

      return result;
    }
  }

  // Select component options
  const getBandtypOptions = () => {
    if (mediatyp !== '' && mediatyp !== undefined) {
      const result = getBandTypes();

      if (result !== undefined && result.length > 0) {
        let bandTyper = result[0].formats.slice(0);
        bandTyper.unshift({
          key: '',
          value: t('valjEllips'),
        });
        return bandTyper.map((entry: any, index: number) => {
          return index === 0 ? (
            <option key={null} value={''}>
              {entry.value}
            </option>
          ) : (
            <option key={entry.key} value={entry.key}>
              {entry.value}
            </option>
          );
        });
      }
    } else {
      return (
        <option key={''} value={''}>
          {t('valjEllips')}
        </option>
      );
    }
  };

  const {
    fields: utgavetyp,
    append: appendUtgavetyp,
    remove: removeUtgavetyp,
  } = useFieldArray({
    control,
    name: 'utgavetyp',
  });

  const updateSelectedUtgavetyp = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    let value: string = (event.target as HTMLInputElement).value;
    setSelectedUtgavetyp(value);
  };

  const addUtgavetyp = () => {
    if (selectedUtgavetyp !== '') {
      const result = window.bokinfo.dictionaries['editionTypes'].filter(
        (obj: any) => {
          return obj.key === selectedUtgavetyp;
        }
      );

      appendUtgavetyp(result);
      setSelectedUtgavetyp('');
    }
  };

  // Typeahead component options
  const getSuggestions = useCallback(
    (queryString: string): void => {

      const result = queryString.length
        ? currentMediaTyp.formats.filter((t: any) => t.value.toLowerCase().includes(queryString.toLowerCase()))
        : currentMediaTyp.formats;

      setBandtyper(result);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentMediaTyp]
  );

  return (
    <>
      <h3 ref={blockRef}>{t('format')}</h3>
      <Col xs={12}>
        <hr className="u-text-grey" />
      </Col>
      <Card className="nopadding my-4">
        <Card.Body className="p-3">
          <Row>
            {pageVariant !== 'quick' && (
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1 text-body">
                    {t('mediatyp')}*
                  </Form.Label>
                  <LockedOrChanged
                    locked={shouldBeLocked(lockedFields, 'mediatyp')}
                    changed={hasChanged(changedProps, 'mediatyp')}
                    showExtendedInfo={showExtendedChangeInfo}
                    changedInformation={getChangedInformation(
                      changedProps,
                      'mediatyp'
                    )}
                    child={
                      <Form.Control
                        as={'select'}
                        type="select"
                        className="form-select"
                        placeholder={t('valjEllips')}
                        {...register('mediatyp', {
                          required: t('valideringMediatyp'),
                        })}
                        isInvalid={!!errors.mediatyp}
                      >
                        {getOptions('mediaTypes')}
                      </Form.Control>
                    }
                  />
                  {errors.mediatyp && (
                    <Form.Control.Feedback type="invalid">
                      {`${errors.mediatyp.message}`}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            )}
            <Col xs={12}>
              {(currentMediaTyp?.allowText || currentMediaTyp.formats?.length > 0) &&
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1 text-body">
                    {t('bandtyp')}{currentMediaTyp.requireFormat && "*"}
                    <InformationIcon
                      text={t('hjalpBandtyp')}
                      inline={true}
                      className="ps-1"
                    />
                  </Form.Label>
                  <LockedOrChanged
                    locked={shouldBeLocked(lockedFields, 'bandtyp')}
                    changed={hasChanged(changedProps, 'bandtyp')}
                    showExtendedInfo={showExtendedChangeInfo}
                    changedInformation={getChangedInformation(
                      changedProps,
                      'bandtyp'
                    )}
                    child={
                      (
                        <>
                          {currentMediaTyp?.allowText && !currentMediaTyp.formats?.length && (
                            <Form.Control
                              {...register('bandtyp', {
                                required: { value: currentMediaTyp.requireFormat, message: t('valideringBandtyp') }
                              })}
                              placeholder={t('skrivEllips')}
                            />)}

                          {currentMediaTyp?.formats?.length > 0 && !currentMediaTyp.allowText && (
                            <Form.Control
                              as={'select'}
                              type="select"
                              {...register('bandtyp', {
                                required: { value: currentMediaTyp.requireFormat, message: t('valideringBandtyp') }
                              })}
                              className="form-select"
                              placeholder={t('valjEllips')}
                              isInvalid={!!errors.bandtyp}
                            >
                              {getBandtypOptions()}
                            </Form.Control>)}

                          {currentMediaTyp?.allowText && currentMediaTyp.formats?.length > 0 && (
                            <AsyncTypeahead
                              id="bandtypTypeahead"
                              inputProps={{ required: currentMediaTyp.requireFormat }}
                              labelKey={'value'}
                              isLoading={false}
                              options={bandtyper}
                              disabled={shouldBeLocked(lockedFields, 'bandtyp')}
                              placeholder={t('skrivEllips')}
                              selected={selectedBandtyp}
                              minLength={0}
                              allowNew={true}
                              newSelectionPrefix={t('anvand')}
                              onFocus={() => {
                                getSuggestions("");
                              }}
                              onSearch={(query: string) => {
                                getSuggestions(query);
                              }}
                              onChange={(selected: any) => {
                                onTypeaheadSelect(selected);
                              }}
                            />)}
                        </>
                      )
                    }
                  />
                  {errors.bandtyp && (
                    <Form.Control.Feedback type="invalid">
                      {`${errors.bandtyp.message}`}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              }
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 text-body">
                    {t('omfang')}
                    <InformationIcon
                        text={t('hjalpOmfang')}
                        inline={true}
                        className="ps-1"
                    />
                </Form.Label>
                <LockedOrChanged
                  locked={shouldBeLocked(lockedFields, 'omfang')}
                  changed={hasChanged(changedProps, 'omfang')}
                  showExtendedInfo={showExtendedChangeInfo}
                  changedInformation={getChangedInformation(
                    changedProps,
                    'omfang'
                  )}
                  child={
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('omfang', {
                        setValueAs: (value: any) => !!value ? value : null,
                        validate: (value: any) => (!isNaN(Number(value)) && Number.isInteger(Number(value))) || `${t('omfang')} ${t('isNotAValidInteger')}`
                      })}
                      isInvalid={!!errors.omfang}
                    />
                  } />
                {errors.omfang && (
                  <Form.Text className="text-danger">
                    {errors.omfang.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 text-body">
                  {t('omfangTyp')}
                </Form.Label>
                <LockedOrChanged
                  locked={shouldBeLocked(lockedFields, 'omfangTyp')}
                  changed={hasChanged(changedProps, 'omfangTyp')}
                  showExtendedInfo={showExtendedChangeInfo}
                  changedInformation={getChangedInformation(
                    changedProps,
                    'omfangTyp'
                  )}
                  child={
                    <Form.Control
                      as={'select'}
                      defaultValue={getValues('omfangTyp')}
                      type="select"
                      className="form-select"
                      placeholder={t('valjEllips')}
                      {...register('omfangTyp', {
                        validate: (value: any) => {
                          const omfangValue = getValues('omfang'); // Hämta värdet från omfang

                          if (!omfangValue) {
                            return true; // Om 'omfang' är tomt är det OK att 'omfangTyp' också är tomt
                          }
                          return !!value || t('valideringOmfangTyp'); // Om 'omfang' finns måste 'omfangTyp' anges
                        },
                      })}
                      isInvalid={!!errors.omfangTyp}
                    >
                      {getOptions('rangeTypes', true)}
                    </Form.Control>
                  }
                />
                {errors.omfangTyp && (
                  <Form.Text className="text-danger">
                    {errors.omfangTyp.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12}>
              <hr className="u-text-grey" />
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 text-body">
                    {t('illustrerad')}
                    <InformationIcon
                        text={t('hjalpIllustrerad')}
                        inline={true}
                        className="ps-1"
                    />
                </Form.Label>
                <LockedOrChanged
                  locked={shouldBeLocked(lockedFields, 'illustrerad')}
                  changed={hasChanged(changedProps, 'illustrerad')}
                  showExtendedInfo={showExtendedChangeInfo}
                  changedInformation={getChangedInformation(
                    changedProps,
                    'illustrerad'
                  )}
                  child={
                    <Form.Control
                      as={'select'}
                      type="select"
                      className="form-select"
                      placeholder={t('valjEllips')}
                      {...register('illustrerad', {
                        setValueAs: (v: any) => (v === 'true' || v === true)
                      })}
                      isInvalid={!!errors.illustrerad}
                    >
                      {getBooleanOptions()}
                    </Form.Control>
                  }
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 text-body">
                    {t('omslagsfarg')}
                    <InformationIcon
                        text={t('hjalpOmslagsfarg')}
                        inline={true}
                        className="ps-1"
                    />
                </Form.Label>
                <LockedOrChanged
                  locked={shouldBeLocked(lockedFields, 'omslagsfarg')}
                  changed={hasChanged(changedProps, 'omslagsfarg')}
                  showExtendedInfo={showExtendedChangeInfo}
                  changedInformation={getChangedInformation(
                    changedProps,
                    'omslagsfarg'
                  )}
                  child={
                    <Form.Control
                      as={'select'}
                      type="select"
                      className="form-select"
                      placeholder={t('valjEllips')}
                      {...register('omslagsfarg')}
                      isInvalid={!!errors.omslagsfarg}
                    >
                      {getOptions('coverColors')}
                    </Form.Control>
                  }
                />
              </Form.Group>
            </Col>
            {pageVariant !== 'quick' && (
              <>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                        {t('bredd')}
                        <InformationIcon
                            text={t('hjalpBredd')}
                            inline={true}
                            className="ps-1"
                        />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('bredd', {
                        setValueAs: (value: any) => !!value ? value : null,
                        validate: (value: any) => (!isNaN(Number(value)) && Number.isInteger(Number(value))) || `${t('bredd')} ${t('isNotAValidInteger')}`
                      })}
                      isInvalid={!!errors.bredd}
                    />
                    {errors.bredd && (
                      <Form.Text className="text-danger">
                        {errors.bredd.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                        {t('hojd')}
                        <InformationIcon
                            text={t('hjalpHojd')}
                            inline={true}
                            className="ps-1"
                        />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('hojd', {
                        setValueAs: (value: any) => !!value ? value : null,
                        validate: (value: any) => (!isNaN(Number(value)) && Number.isInteger(Number(value))) || `${t('hojd')} ${t('isNotAValidInteger')}`
                      })}
                      isInvalid={!!errors.hojd}
                    />
                    {errors.hojd && (
                      <Form.Text className="text-danger">
                        {errors.hojd.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                        {t('ryggbredd')}
                        <InformationIcon
                            text={t('hjalpRyggbredd')}
                            inline={true}
                            className="ps-1"
                        />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('ryggbredd', {
                        setValueAs: (value: any) => !!value ? value : null,
                        validate: (value: any) => (!isNaN(Number(value)) && Number.isInteger(Number(value))) || `${t('ryggbredd')} ${t('isNotAValidInteger')}`
                      })}
                      isInvalid={!!errors.ryggbredd}
                    />
                    {errors.ryggbredd && (
                      <Form.Text className="text-danger">
                        {errors.ryggbredd.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                        {t('vikt')}
                        <InformationIcon
                            text={t('hjalpVikt')}
                            inline={true}
                            className="ps-1"
                        />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('vikt', {
                        setValueAs: (value: any) => !!value ? value : null,
                        validate: (value: any) => (!isNaN(Number(value)) && Number.isInteger(Number(value))) || `${t('vikt')} ${t('isNotAValidInteger')}`
                      })}
                      isInvalid={!!errors.vikt}
                    />
                    {errors.vikt && (
                      <Form.Text className="text-danger">
                        {errors.vikt.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="mb-1 text-body">
                        {t("viktExklusiveForpackning")}
                        <InformationIcon
                            text={t('hjalpViktExklusiveForpackning')}
                            inline={true}
                            className="ps-1"
                        />
                    </Form.Label>
                    <Form.Control
                      placeholder={t('skrivEllips')}
                      {...register('viktExklusiveForpackning', {
                        setValueAs: (value: any) => {
                          if (!value) return null; // Tillåt tomma värden
                          try {
                            return parseSwedishDecimal(value); // Försök att parsa värdet
                          } catch (error: any) {
                            return error; // Returnera felmeddelandet om parsing misslyckas
                          }
                        },
                        validate: (value: any) => {
                          return !isNaN(Number(value)) || `${t('viktExklusiveForpackning')} ${t('isNotAValidNumber') || value}`
                        }
                      })}
                      isInvalid={errors.viktExklusiveForpackning}
                    />
                    {errors.viktExklusiveForpackning && (
                      <Form.Text className="text-danger">
                        {errors.viktExklusiveForpackning.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </>
            )}
            <Col xs={6}>
              <div className="d-flex align-items-end w-100">
                <Form.Group as={Col} className="mb-3 col me-3">
                  <Form.Label className="mb-1 text-body">
                    {t("typAvUtgava")}
                  </Form.Label>
                  <Form.Control
                    as={'select'}
                    type="select"
                    className="form-select"
                    placeholder={t('valjEllips')}
                    value={selectedUtgavetyp}
                    onChange={updateSelectedUtgavetyp}
                  >
                    {getOptions('editionTypes')}
                  </Form.Control>
                </Form.Group>
                <Form.Group as={Col} className="mb-3 col-auto">
                  <Button
                    className="primary"
                    type="button"
                    onClick={addUtgavetyp}
                  >
                    <i className="bi bi-plus"></i>
                  </Button>
                </Form.Group>
              </div>
              {utgavetyp.map((row: any, index: number) => {
                return (
                  <Row className={`${index % 2 === 1 ? 'u-background-grey' : ''}`}>
                    <Col xs={11} className="d-flex align-items-center">
                      {getOptionsLabel(
                        "editionTypes",
                        getValues(`utgavetyp.${index}.key`)
                      )}
                    </Col>
                    <Col xs={1} className="d-flex align-items-center">
                      <i
                        className="bi bi-trash3 u-text-error u-cursor-pointer mx-auto"
                        onClick={() => removeUtgavetyp(index)}
                      />
                    </Col>
                  </Row>
                )
              })}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};
